import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, of as observableOf, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { FeatureFlags, FeatureFlagVariants } from '@app/core/feature-flags/feature-flags';
import { LaunchDarklyService } from '@app/core/feature-flags/launchdarkly.service';

import { EnterpriseRegistration } from '../enterprise-registration';
import { EnterpriseRegistrationAnalyticsService } from '../enterprise-registration-analytics.service';
import { RegistrationStep } from '../registration-step';
import { StepName } from '../registration-step-name';
import { VirtualCareConfirmationStepComponent } from './virtual-care-confirmation-step.component';

@Injectable()
export class VirtualCareConfirmationConfig extends RegistrationStep {
  GA_LABEL = 'VirtualCareConfirmation_Step';
  MODULE = 'VirtualCareConfirmation';

  component = VirtualCareConfirmationStepComponent;
  progress = 10;
  showLoginLink = true;

  form: UntypedFormGroup = this.formBuilder.group({});

  constructor(
    private analyticsService: EnterpriseRegistrationAnalyticsService,
    private formBuilder: UntypedFormBuilder,
    private launchDarklyService: LaunchDarklyService,
    private router: Router,
  ) {
    super();
  }

  canGoBack(): boolean {
    return true;
  }

  initComponent(component: VirtualCareConfirmationStepComponent, _enterpriseRegistration: EnterpriseRegistration) {
    component.form = this.form;
    this.trackPageVisit();
  }

  trackPageVisit() {
    this.analyticsService.pageViewed({ module: this.MODULE });
  }

  submit(state: EnterpriseRegistration): Observable<any> {
    this.trackSubmission(state);

    // If the previous page was 'personal-details', navigate to the 'account-setup' page.
    // Otherwise, set the next page based on the 'work-email' page configuration.
    const previousStep = state.getPreviousStep();
    if (previousStep && previousStep.GA_LABEL === 'PersonalDetails_Step') {
      // Check enterprise registration simplification flag before proceeding to accountSetUp
      return from(
        this.launchDarklyService.updateAnonUserCustomAttributes({
          b2bCompanyId: state.details.b2bCompanyId,
        }),
      ).pipe(
        switchMap(() =>
          this.launchDarklyService
            .featureFlag$(FeatureFlags.ENTERPRISE_REGISTRATION_SIMPLIFICATION, FeatureFlagVariants.OFF)
            .pipe(
              switchMap((flag: FeatureFlagVariants) => {
                const isEnabled = flag !== FeatureFlagVariants.OFF && (flag as unknown) !== false;
                if (isEnabled) {
                  this.router.navigate(['/registration/redirect']);
                  return observableOf(true);
                }
                state.setCurrentStep(StepName.accountSetUp);
                return observableOf(true);
              }),
            ),
        ),
      );
    } else {
      return this.setNextStep(state);
    }
  }

  private trackSubmission(state: EnterpriseRegistration) {
    this.analyticsService.regInputSubmitted({
      isWhitelist: state.isWhitelisted,
      module: this.MODULE,
    });
  }

  private setNextStep(state: EnterpriseRegistration): Observable<any> {
    const whitelistedEmployee = state.whitelistedEmployee;

    if (!whitelistedEmployee) {
      state.setCurrentStep(StepName.activationCode);
      return observableOf(true);
    }

    const employeeB2bCompany = whitelistedEmployee.b2bCompany;

    // Whitelisted employees that have a different workEmail and employeeId are part of an organization
    // that requires them to enter their employee id, redirect them to that page.
    if (whitelistedEmployee.employeeId !== whitelistedEmployee.workEmail) {
      state.patchParams({ b2b_company_id: employeeB2bCompany.id });
      state.setCurrentStep(StepName.employeeId);
      return observableOf(true);
    }
    if (state.shouldConvertAccount()) {
      return state.submitAccountConversion(null, employeeB2bCompany.includesDependent);
    } else {
      if (employeeB2bCompany.includesDependent) {
        state.setCurrentStep(StepName.membershipSelection);
        return observableOf(true);
      } else {
        // Check enterprise registration simplification flag before proceeding to accountSetUp
        return from(
          this.launchDarklyService.updateAnonUserCustomAttributes({
            b2bCompanyId: state.details.b2bCompanyId,
          }),
        ).pipe(
          switchMap(() =>
            this.launchDarklyService
              .featureFlag$(FeatureFlags.ENTERPRISE_REGISTRATION_SIMPLIFICATION, FeatureFlagVariants.OFF)
              .pipe(
                switchMap((flag: FeatureFlagVariants) => {
                  const isEnabled = flag !== FeatureFlagVariants.OFF && (flag as unknown) !== false;
                  if (isEnabled) {
                    this.router.navigate(['/registration/redirect']);
                    return observableOf(true);
                  }
                  state.setCurrentStep(StepName.accountSetUp);
                  return observableOf(true);
                }),
              ),
          ),
        );
      }
    }
  }
}
